import React, { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import {
  inputClassName,
  labelClassName,
  projectFieldValidation,
} from "../modules/projects/new/first";
import { emailValidationPattern } from "../modules/account/signup";
import { Button } from "rizzui/button";
import { Text } from "rizzui/typography";
import { useModal } from "../hooks/useModal";
import Select from "react-select";
import { ThemeContext } from "../context/themeContext";
import { useCampaigns } from "../hooks/useCampaign";
import cn from "../lib/utils/class-name";
import { useColorStyle } from "../hooks/useColorStyle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/authContext";
import { useLocation, useNavigate } from "react-router";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useNotifcication } from "../hooks/useNotification";
import CustomPhoneInput from "./phoneInput";
import ReviewContact from "./reviewSingleEntry";

export default function SingleEntry({ id }: { id?: string }) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  const location = useLocation();

  const { theme } = useContext(ThemeContext);
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();
  const { colorStyles } = useColorStyle(theme);
  const { openNotification } = useNotifcication();
  const { view, closeModal, openModal } = useModal();
  useEffect(() => {
    reset();
    entryReset();
  }, [view]);

  const { data, isLoading } = useCampaigns();
  const queryClient = useQueryClient();
  const campaigns = data?.map((campaign) => ({
    value: campaign._id,
    label: campaign.campaign_name,
  }));

  const {
    mutate: createLead,
    isPending: entryLoading,
    error: entryError,
    reset: entryReset,
  } = useMutation({
    mutationFn: async () => {
      const {
        firstname,
        lastname,
        phone_number,
        email,
        campaign_id,
        company_name,
        code,
      } = getValues();

      try {
        const response = await axios.post(
          `${baseUrl}/leads`,
          {
            firstname,
            lastname,
            phone_number: "+" + phone_number,
            email,
            campaign_id: campaign_id.value,
            company_name,
            country_code: code,
          },
          { headers: { Authorization: AuthStr } }
        );

        if (response?.data?.success) {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === "leads",
          });
          queryClient.invalidateQueries({
            queryKey: [id, "campaign_analytics", token],
          });
          openNotification({
            status: "success",
            message: "Lead Added",
          });
          location?.pathname === "/campaigns/new" &&
            setTimeout(() => navigate("/campaigns"), 1000);
          closeModal();
        }

        return response;
      } catch (e) {
        if (e?.response?.data?.message === "Session expired") {
          navigate("/account/logout");
        } else if (
          e?.response?.data?.details?.message ===
          "This record already exists in another campaign do you still want to add it to this campaign?"
        ) {
          openModal({
            view: (
              <ReviewContact
                overrideKey={e?.response?.data?.details?.details?.override_key}
                details={{
                  firstname,
                  lastname,
                  phone_number: "+" + phone_number,
                  email,
                  campaign_id: campaign_id.value,
                  company_name,
                  country_code: code,
                }}
              />
            ),
          });
        }

        throw new Error(e?.response?.data?.message ?? "Something went wrong");
      }
    },
  });

  useEffect(() => {
    if (id && campaigns?.length) {
      const selectedCampaign = campaigns.find(
        (campaign: { value: string; label: string }) => campaign.value === id
      );
      const campaign_id = getValues("campaign_id");
      if (selectedCampaign && selectedCampaign.value !== campaign_id?.value) {
        setValue("campaign_id", selectedCampaign);
      }
    }
  }, [id, campaigns]);

  const handleEntry = handleSubmit((data) => {
    createLead();
  });

  return (
    <div className="md:w-[471px] w-[96%] bg-background dark:bg-[#2A2C30] rounded-[8px] p-[32px] mx-auto flex flex-col gap-[28px]">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <h1 className="text-primary-dark font-ibmPlexSerif font-[600] text-[18px] leading-[23.4px]">
        Single Entry
      </h1>
      <form
        className="flex flex-col gap-[24px]"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleEntry();
        }}
      >
        <div className="grid grid-cols-2 gap-[15px]">
          <Input
            type="text"
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            label="First name"
            {...register("firstname", {
              ...projectFieldValidation,
            })}
            error={
              errors.firstname?.message
                ? "First name " + errors.firstname.message
                : ""
            }
          />
          <Input
            type="text"
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            label="Last name"
            {...register("lastname", {
              ...projectFieldValidation,
            })}
            error={
              errors.lastname?.message
                ? "Last name " + errors.lastname.message
                : ""
            }
          />
        </div>
        <Controller
          control={control}
          name="phone_number"
          rules={{
            required: {
              value: true,
              message: "Phone Number is Required",
            },
            pattern: {
              value: /^.{5,}$/,
              message: "Enter a valid phone number",
            },
          }}
          render={({ field: { onChange, value } }) => (
            <div className="w-full flex flex-col gap-[8px]">
              <label className={labelClassName}>Phone number</label>
              <CustomPhoneInput
                onChange={({ phone, code }) => {
                  setValue("code", code);
                  onChange(phone); // Update phone and country
                }}
                value={value ?? null}
              />
              {errors.phone_number?.message && (
                <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                  {"" + errors.phone_number.message}
                </p>
              )}
            </div>
          )}
        />

        {/*        <Input
          type="text"
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          label="Phone number"
          {...register("phone_number", {
            ...projectFieldValidation,
          })}
          error={
            errors.phone_number?.message
              ? "Phone number " + errors.phone_number.message
              : ""
          }
        /> */}

        <Input
          type="email"
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          label="Email address"
          {...register("email", {
            ...emailValidationPattern,
          })}
          error={errors.email?.message ? "" + errors.email.message : ""}
        />

        <Input
          type="text"
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          label="Company name (optional)"
          {...register("company_name")}
          error={
            errors.company_name?.message ? "" + errors.company_name.message : ""
          }
        />

        <div>
          <Text className={cn(labelClassName, "mb-1")}>Campaign name</Text>
          <Controller
            control={control}
            name="campaign_id"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div>
                <Select
                  options={campaigns}
                  placeholder=""
                  styles={colorStyles}
                  isSearchable={true}
                  isDisabled={id ? true : false}
                  value={value ?? null}
                  name={name}
                  onChange={(e) => {
                    onChange(e);
                  }}
                  isLoading={isLoading}
                />
                {errors.campaign_id?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.campaign_id.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <Button
          type="submit"
          className="!bg-btn w-full font-switzer font-[500] text-[12px] leading-[15.84px] text-foreground"
          isLoading={entryLoading}
        >
          Submit
        </Button>
        {entryError?.message && (
          <Text className="text-red text-[12px] font-switzer font-[400]">
            {entryError?.message}
          </Text>
        )}
      </form>
    </div>
  );
}
